import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import BakerIngredientBreakdown from "@/components/BakerIngredientBreakdown";
import CalculatorStepper from "@/components/CalculatorStepper";
import CalculatorFlavorSelect from "@/components/CalculatorFlavorSelect";
import CalculatorHeader from "@/components/CalculatorHeader";
import { PRODUCTION_DRIVER_PAGE_CSS } from "@/lib/productionDriverPageStyles";
import { fetchBakerCalculator, fetchCalculatorFlavors } from "@/lib/calculatorApi";

const BASE_OPTIONS = ["Maida", "Aata"];
const SWEETENER_OPTIONS = ["Sugar", "Icing Sugar"];

import { snapCalculatorWeight } from "@/lib/calculatorWeight";

export default function BakerCalculator({ embedded = false }) {
  const [flavors, setFlavors] = useState([]);
  const [flavor, setFlavor] = useState("");
  const [weightKg, setWeightKg] = useState("0.5");
  const [baseFlour, setBaseFlour] = useState("Maida");
  const [sweetener, setSweetener] = useState("Sugar");
  const [breakdown, setBreakdown] = useState(null);
  const [loadingFlavors, setLoadingFlavors] = useState(true);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingFlavors(true);
        const list = await fetchCalculatorFlavors();
        if (cancelled) return;
        setFlavors(list);
        if (list.length > 0) {
          setFlavor((prev) => prev || list[0].flavor);
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || String(e));
      } finally {
        if (!cancelled) setLoadingFlavors(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const weightNum = useMemo(() => snapCalculatorWeight(weightKg), [weightKg]);

  useEffect(() => {
    if (!flavor) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingBreakdown(true);
        setErr("");
        const data = await fetchBakerCalculator({
          flavor,
          weightKg: weightNum,
          baseFlour,
          sweetener,
        });
        if (!cancelled) setBreakdown(data);
      } catch (e) {
        if (!cancelled) {
          setErr(e.message || String(e));
          setBreakdown(null);
        }
      } finally {
        if (!cancelled) setLoadingBreakdown(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flavor, weightNum, baseFlour, sweetener]);

  return (
    <div
      className={`driver-page${embedded ? " driver-page--embedded" : ""}`}
      style={
        embedded
          ? { minHeight: 0, padding: "0 0 24px", maxWidth: "none", margin: 0 }
          : undefined
      }
    >
      <style>{PRODUCTION_DRIVER_PAGE_CSS}</style>
      {embedded ? (
        <style>{`
          .driver-page--embedded .driver-card:first-of-type { margin-top: 0; }
        `}</style>
      ) : null}

      {!embedded ? (
        <CalculatorHeader
          title="Baker calculator"
          otherLabel="Froster →"
          otherTo="/calculator/froster"
        />
      ) : null}

      <div className="driver-card">
        <div className="driver-label">Flavor</div>
        <CalculatorFlavorSelect
          flavors={flavors}
          value={flavor}
          onChange={setFlavor}
          loading={loadingFlavors}
        />

        <div className="mt-4">
          <div className="driver-label">Weight (kg)</div>
          <CalculatorStepper
            value={weightKg}
            onChange={setWeightKg}
            min={0.25}
            max={20}
            step={0.5}
            decimals={2}
            calculatorWeight
          />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <div>
            <div className="driver-label">Cake base</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {BASE_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setBaseFlour(opt)}
                  className="driver-frost-tag driver-frost-tag--custom"
                  style={baseFlour === opt ? undefined : { opacity: 0.45 }}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="driver-label">Sweetener</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {SWEETENER_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setSweetener(opt)}
                  className="driver-frost-tag driver-frost-tag--rtg"
                  style={sweetener === opt ? undefined : { opacity: 0.45 }}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>

        {flavor ? (
          <div className="driver-frost-line mt-3">
            <span className="driver-frost-chip driver-frost-chip--flavor">{flavor}</span>
            <span className="driver-frost-chip driver-frost-chip--weight">{weightKg} kg</span>
          </div>
        ) : null}
      </div>

      {err ? <div className="driver-error">{err}</div> : null}

      <div className="driver-card" style={{ position: "relative" }}>
        {loadingBreakdown ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[18px] bg-white/70">
            <Loader2 className="h-6 w-6 animate-spin text-[#EE2677]" />
          </div>
        ) : null}
        <BakerIngredientBreakdown
          breakdown={breakdown}
          baseFlour={baseFlour}
          sweetener={sweetener}
        />
      </div>
    </div>
  );
}
